import React, { FC, createContext, useContext, useState, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { DragDropContext, DropResult, DragStart, DragUpdate } from "@hello-pangea/dnd";
import { useAppStore } from "@/store";

interface TabBarContextProviderProps {
  children?: React.ReactNode;
}

interface TabDragContextType {
  isAnyTabDragging: boolean;
}

const TabDragContext = createContext<TabDragContextType>({ isAnyTabDragging: false });

export const useTabDragContext = () => useContext(TabDragContext);

export const TabBarContextProvider: FC<TabBarContextProviderProps> = observer(({ children }) => {
  const { store } = useAppStore();
  const [isAnyTabDragging, setIsAnyTabDragging] = useState(false);

  const handleDragStart = useCallback((_result: DragStart) => {
    setIsAnyTabDragging(true);
  }, []);

  const handleDragUpdate = useCallback((_result: DragUpdate) => {
    setIsAnyTabDragging(true);
  }, []);

  const handleDragEnd = useCallback(
    (result: DropResult) => {
      setIsAnyTabDragging(false);

      const { source, destination } = result;

      // If no destination or same position, do nothing
      if (
        !destination ||
        (source.droppableId === destination.droppableId && source.index === destination.index)
      ) {
        return;
      }

      const tabId = result.draggableId;
      const sourcePaneId = source.droppableId;
      const targetPaneId = destination.droppableId;
      const targetIndex = destination.index + 1; // Offset by 1 to add the default tab

      // Find the actual tab being moved
      const sourcePane = store.navigation.tabs.panesMap.get(sourcePaneId);
      if (!sourcePane) return;

      const sourcePaneTabs = store.navigation.tabs.getTabsForPane(sourcePaneId);
      const movedTab = sourcePaneTabs.find(tab => tab.id === tabId);
      if (!movedTab) return;

      store.navigation.tabs.moveTabToPane({
        tabId: movedTab.id,
        targetPaneId,
        targetIndex,
      });
    },
    [store.navigation.tabs]
  );

  const contextValue: TabDragContextType = {
    isAnyTabDragging,
  };

  return (
    <TabDragContext.Provider value={contextValue}>
      <DragDropContext
        onDragStart={handleDragStart}
        onDragUpdate={handleDragUpdate}
        onDragEnd={handleDragEnd}
      >
        {children}
      </DragDropContext>
    </TabDragContext.Provider>
  );
});
